import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import api from "../../lib/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

const inputCls =
  "w-full bg-white border border-black/15 px-3 py-2.5 text-sm font-light focus:outline-none focus:border-black";

const emptyForm = () => ({
  name: "",
  phone: "",
  email: "",
  service_id: "",
  staff: "Indiferente",
  date: todayISO(),
  time: "",
  notes: "",
  notify: false,
});

export const AdminBookingModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm());
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [clients, setClients] = useState([]);
  const [avail, setAvail] = useState(null); // {slots, closed, duration_min} | "error" | null
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/booking-services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/admin/professionals").then((r) => setProfessionals(r.data || [])).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  // Pré-visualização de "cabimento" (slots livres/ocupados) — apenas informativa
  const fetchAvailability = useCallback(() => {
    if (!form.service_id || !form.date) {
      setAvail(null);
      return;
    }
    setLoadingAvail(true);
    api
      .get("/bookings/availability", {
        params: { date: form.date, service_id: form.service_id, staff: form.staff },
      })
      .then((r) => setAvail(r.data))
      .catch(() => setAvail("error"))
      .finally(() => setLoadingAvail(false));
  }, [form.service_id, form.date, form.staff]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Indique o nome do cliente.");
      return;
    }
    if (!form.service_id) {
      toast.error("Escolha o serviço.");
      return;
    }
    if (!form.time || !/^\d{2}:\d{2}$/.test(form.time)) {
      toast.error("Indique a hora (HH:MM).");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
      service_id: form.service_id,
      staff: form.staff,
      date: form.date,
      time: form.time,
      notes: form.notes.trim() || null,
      notify: form.notify,
    };
    try {
      await api.post("/admin/bookings", payload);
      toast.success("Marcação criada.");
      onCreated && onCreated();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível criar a marcação.");
    } finally {
      setSaving(false);
    }
  };

  const isBusyTime = (t) => {
    if (!avail || avail === "error" || !avail.slots) return false;
    const slot = avail.slots.find((s) => s.time === t);
    return slot ? !slot.available : false;
  };

  return (
    <div
      data-testid="admin-booking-overlay"
      className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        data-testid="admin-booking-form"
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-black/10 p-6 md:p-8 space-y-5 w-full max-w-3xl my-8 animate-pop-in shadow-xl"
      >
        <div className="flex items-center justify-between">
          <p className="label-editorial">Nova marcação (interna)</p>
          <button type="button" onClick={onClose} data-testid="admin-booking-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="label-editorial text-black/50 block mb-2">Cliente</label>
            <input
              data-testid="ab-name"
              required
              list="ab-clients-list"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome do cliente"
              className={inputCls}
            />
            <datalist id="ab-clients-list">
              {clients.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="label-editorial text-black/50 block mb-2">Telefone</label>
            <input
              data-testid="ab-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(opcional)"
              className={inputCls}
            />
          </div>
          <div>
            <label className="label-editorial text-black/50 block mb-2">Email</label>
            <input
              data-testid="ab-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="(opcional)"
              className={inputCls}
            />
          </div>
          <div>
            <label className="label-editorial text-black/50 block mb-2">Serviço</label>
            <select
              data-testid="ab-service"
              required
              value={form.service_id}
              onChange={(e) => setForm({ ...form, service_id: e.target.value })}
              className={inputCls}
            >
              <option value="">Escolher…</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.duration_min} min
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-editorial text-black/50 block mb-2">Profissional</label>
            <select
              data-testid="ab-staff"
              value={form.staff}
              onChange={(e) => setForm({ ...form, staff: e.target.value })}
              className={inputCls}
            >
              <option value="Indiferente">Indiferente</option>
              {professionals.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-editorial text-black/50 block mb-2">Data</label>
            <input
              data-testid="ab-date"
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>

        {/* Cabimento de horários */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="label-editorial text-black/50">Hora / Cabimento</label>
            {loadingAvail && <Loader2 size={14} className="animate-spin text-black/40" />}
          </div>
          {avail && avail !== "error" && avail.closed && (
            <p data-testid="ab-closed" className="text-xs text-red-600 mb-2">
              Sem horário de trabalho neste dia — pode encaixar manualmente abaixo.
            </p>
          )}
          {avail && avail !== "error" && !avail.closed && avail.slots && avail.slots.length > 0 && (
            <div data-testid="ab-slots" className="flex flex-wrap gap-1.5 mb-3">
              {avail.slots.map((s) => {
                const selected = form.time === s.time;
                return (
                  <button
                    key={s.time}
                    type="button"
                    data-testid={`ab-slot-${s.time}`}
                    onClick={() => setForm({ ...form, time: s.time })}
                    className={`px-2.5 py-1.5 text-xs border transition-colors ${
                      selected
                        ? "bg-black text-white border-black"
                        : s.available
                          ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                          : "bg-red-50 border-red-200 text-red-500 line-through hover:bg-red-100"
                    }`}
                    title={s.available ? "Livre" : "Ocupado — clique para encaixar (override)"}
                  >
                    {s.time}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-3">
            <input
              data-testid="ab-time"
              type="time"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className={`${inputCls} max-w-[160px]`}
            />
            {form.time && isBusyTime(form.time) && (
              <span data-testid="ab-override-warn" className="text-xs text-amber-600">
                Horário ocupado — será encaixado à força.
              </span>
            )}
          </div>
          <p className="text-[11px] text-black/40 mt-2">
            Verde = livre · Vermelho = ocupado (pode encaixar à força). O admin pode marcar qualquer hora.
          </p>
        </div>

        <div>
          <label className="label-editorial text-black/50 block mb-2">Notas</label>
          <input
            data-testid="ab-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="(opcional)"
            className={inputCls}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-black/60 cursor-pointer">
          <input
            data-testid="ab-notify"
            type="checkbox"
            checked={form.notify}
            onChange={(e) => setForm({ ...form, notify: e.target.checked })}
          />
          Enviar email de confirmação ao cliente (requer email + SMTP configurado)
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="label-editorial px-6 py-3 border border-black/15 hover:bg-black/5"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="ab-submit"
            disabled={saving}
            className="label-editorial bg-black text-white px-6 py-3 hover:bg-black/80 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Criar marcação
          </button>
        </div>
      </form>
    </div>
  );
};
